import { PERCH_FUNNEL_ID } from '~/funnels/perch-energy/config';
import { CustomerAccountType } from '~/typings/graphql';

export type SolarProjectDetails = {
  address?: string;
  annualKwhProduction?: number;
  assignmentOrder?: number;
  ciCapacityAvailable?: boolean;
  clientAccountId?: string;
  codDate?: string | null;
  collectPayment?: boolean;
  paymentProcessor?: string;
  conversionRatio?: number;
  defaultProductId?: string;
  demandProductId?: string | null;
  doNotAskForPaymentMethod?: boolean;
  doNotUnderwriteResi?: boolean;
  entityName?: string;
  estimatedCodDateManual?: string;
  estimatedProjectCreditValue?: number;
  expectedYieldKwhKw?: number;
  externalDisplayName?: string | null;
  ficoUnderwriting?: boolean;
  finDocsUnderwriting?: boolean;
  initialServiceProvider?: string;
  ira?: boolean;
  lmiCapacityAvailable?: boolean;
  lmiProductId?: string;
  lmiProductDiscountPercent?: number;
  name?: string;
  nmcDiscountPercent?: number;
  open?: boolean;
  product?: string;
  programName?: string;
  projectCapacityAvailableKwDc?: number;
  projectLocation?: string;
  projectSizeAc?: number;
  projectSizeDc?: number;
  resiCapacityAvailable?: boolean;
  serviceTerritories?: string;
  sizeRatio?: number;
  smallCsCapacityAvailable?: boolean;
  sssId?: string;
  suppressFicoCheckForLmi?: boolean;
  underwrite?: boolean;
  utilityId?: string;
  utilityNmcTariffId?: string;
  valueOfNetMeteringCredit?: number;
  waitlist?: boolean;
};

export type CountyProjectMatch = 'county_filtered' | 'unrestricted_fallback' | 'no_county';

export const utilityScreenCountyLogFields = (
  countyId: number | undefined,
  countyProjectMatch: CountyProjectMatch | undefined,
): { countyId: number | null; countyProjectMatch: CountyProjectMatch | null } => ({
  countyId: countyId ?? null,
  countyProjectMatch: countyProjectMatch ?? null,
});

type StateLike = {
  abbreviation?: string | null;
};

type UtilityLike = {
  state?: StateLike | null;
};

type PerchAiValidationProjectDetails = {
  ira?: boolean | null;
  lmiCapacityAvailable?: boolean | null;
  lmiProductId?: string | null;
  resiCapacityAvailable?: boolean | null;
  smallCsCapacityAvailable?: boolean | null;
};

type PerchAiValidationEligibilityContext = {
  customerType?: CustomerAccountType;
  funnelId?: string;
  resolvedPerchProductId?: string;
  funnelConfiguration?: {
    projectDetails?: PerchAiValidationProjectDetails | null;
    supportedCustomerTypes?: Array<string | null> | null;
  } | null;
  funnelPacket?: {
    projectSnapshot?: PerchAiValidationProjectDetails | null;
    signupContractTemplate?: {
      supportedCustomerTypes?: Array<string | null> | null;
    } | null;
    utility?: UtilityLike | null;
  } | null;
  utility?: UtilityLike | null;
  utilitySelection?: UtilityLike | null;
};

const AI_VALIDATION_ELIGIBLE_STATES = ['DE', 'IL', 'MA', 'MD', 'MN', 'NY'] as const;

type AiValidationEligibleState = (typeof AI_VALIDATION_ELIGIBLE_STATES)[number];

const getStateAbbreviation = (state?: StateLike | null): string | undefined => {
  const abbreviation = state?.abbreviation?.trim().toUpperCase();
  return abbreviation || undefined;
};

const isAiValidationEligibleState = (
  abbreviation?: string,
): abbreviation is AiValidationEligibleState =>
  abbreviation != null &&
  (AI_VALIDATION_ELIGIBLE_STATES as readonly string[]).includes(abbreviation);

const getUtilityStateAbbreviation = (
  context: PerchAiValidationEligibilityContext,
): string | undefined =>
  getStateAbbreviation(context.utility?.state) ??
  getStateAbbreviation(context.utilitySelection?.state) ??
  getStateAbbreviation(context.funnelPacket?.utility?.state);

// AI validation follows single-account Perch flows for eligible states.
export function isEligibleForAIValidation(
  context: PerchAiValidationEligibilityContext,
  options: {
    allowsMultipleAccounts?: boolean;
  } = {},
): boolean {
  if (!context) return false;
  // Only perch-energy funnel for AI validation
  if (context.funnelId !== PERCH_FUNNEL_ID) return false;
  // No business customers for AI validation
  if (context.customerType === CustomerAccountType.Business) return false;
  // No multiple-account enrollments for AI validation
  if (options.allowsMultipleAccounts) return false;

  // Only eligible states for AI validation (DE/IL/MA/MD/MN/NY)
  const stateAbbreviation = getUtilityStateAbbreviation(context);
  if (!isAiValidationEligibleState(stateAbbreviation)) return false;

  // DE/IL/MA/MD/MN/NY: single-account non-business are eligible for AI validation
  return true;
}

/**
 * Determines if the Perch funnel should use capacity-driven logic based on projectDetails.
 *
 * The backend only returns projectDetails with capacity flags for IL/MN/OR states when
 * no perchSfProductId is provided. So we simply check if projectDetails with capacity
 * flags exists - if yes, use capacity-driven logic; if no, show both options.
 *
 * @param projectDetails - The project details from funnel configuration
 * @returns true if projectDetails has capacity flags (capacity-driven), false otherwise
 */
export interface PerchCapacityAvailability {
  readonly resi: boolean;
  readonly smallCs: boolean;
  readonly lmi: boolean;
  readonly ci: boolean;
}

export function isPerchCapacityDriven(projectDetails: SolarProjectDetails | undefined): boolean {
  if (!projectDetails) return false;

  // Check if any capacity flags are defined (not just checking if they're true)
  return (
    projectDetails.resiCapacityAvailable !== undefined ||
    projectDetails.smallCsCapacityAvailable !== undefined ||
    projectDetails.lmiCapacityAvailable !== undefined ||
    projectDetails.ciCapacityAvailable !== undefined
  );
}

const emptyCapacityAvailability = (): PerchCapacityAvailability => ({
  resi: false,
  smallCs: false,
  lmi: false,
  ci: false,
});

export function getPerchCapacityAvailability(
  details: SolarProjectDetails | undefined,
): PerchCapacityAvailability {
  if (!details) {
    return emptyCapacityAvailability();
  }
  return {
    resi: details.resiCapacityAvailable === true,
    smallCs: details.smallCsCapacityAvailable === true,
    lmi: details.lmiCapacityAvailable === true,
    ci: details.ciCapacityAvailable === true,
  };
}

export function hasAnyPerchCapacityFlag(details: SolarProjectDetails | undefined): boolean {
  const c = getPerchCapacityAvailability(details);
  return c.resi || c.smallCs || c.lmi || c.ci;
}

export function isAbpProgram(details: { programName?: string | null } | null | undefined): boolean {
  if (!details) {
    return false;
  }
  const programName = details.programName?.toLowerCase();
  return ['abp', 'cdcs-abp'].includes(programName ?? '');
}

/**
 * Returns the savings percent tied to an explicitly passed product id.
 *
 * When a `perchSfProductId` is supplied we must not infer savings from capacity flags alone: the
 * project snapshot can advertise LMI capacity while the requested product is the resi/default one.
 * Matching the product id against the snapshot's `lmiProductId` / `defaultProductId` guarantees we
 * surface the discount that actually applies to the requested product.
 *
 * Returns null when the product id doesn't match either snapshot product id (callers fall back to
 * the capacity-driven behavior).
 */
function resolveProductIdSavingsPercent(
  projectDetails: SolarProjectDetails | undefined,
  perchSfProductId: string,
): number | null {
  if (!projectDetails) return null;

  if (projectDetails.lmiProductId && projectDetails.lmiProductId === perchSfProductId) {
    return projectDetails.lmiProductDiscountPercent ?? null;
  }

  if (projectDetails.defaultProductId && projectDetails.defaultProductId === perchSfProductId) {
    return projectDetails.nmcDiscountPercent ?? null;
  }

  return null;
}

/**
 * Returns the correct savings percent for a capacity-driven project based on the selected customer
 * type. Returns null for non-capacity-driven configs or when the customer type is not yet chosen.
 *
 * When `perchSfProductId` is provided, the product id takes precedence: the discount is resolved by
 * matching it against the snapshot's `lmiProductId` (LMI discount) or `defaultProductId`
 * (NMC discount). If it matches neither, we retain the existing capacity-driven behavior.
 */
export function resolveCapacityDrivenSavingsPercent(
  projectDetails: SolarProjectDetails | undefined,
  customerType: CustomerAccountType | undefined,
  perchSfProductId?: string,
): number | null {
  if (perchSfProductId) {
    const productIdPercent = resolveProductIdSavingsPercent(projectDetails, perchSfProductId);
    if (productIdPercent != null) return productIdPercent;
  }

  if (!isPerchCapacityDriven(projectDetails) || !customerType) return null;
  const { lmi } = getPerchCapacityAvailability(projectDetails);
  if (
    customerType === CustomerAccountType.Residential &&
    lmi &&
    projectDetails?.lmiProductDiscountPercent != null
  ) {
    return projectDetails.lmiProductDiscountPercent;
  }
  if (customerType === CustomerAccountType.Business && projectDetails?.nmcDiscountPercent != null) {
    return projectDetails.nmcDiscountPercent;
  }
  return null;
}
